import cx from "classnames";
import { useMemo } from "react";
import { Link } from "react-router";
import { t } from "ttag";

import { Table } from "metabase/admin/components/table";
import AdminS from "metabase/css/admin.module.css";
import CS from "metabase/css/core/index.css";
import { FormMessage } from "metabase/forms";
import { isSyncCompleted } from "metabase/lib/syncing";
import { PLUGIN_FEATURE_LEVEL_PERMISSIONS } from "metabase/plugins";
import { Box, Button, Flex, Icon, Loader, UnstyledButton } from "metabase/ui";
import type { Database, Engine } from "metabase-types/api";
import { color, darken } from "metabase/lib/colors";

const query = {
  ...PLUGIN_FEATURE_LEVEL_PERMISSIONS.databaseDetailsQueryProps,
};

interface DatabaseListProps {
  databases: Database[];
  engines: Record<string, Engine>;
  deletes: Array<any>;
  deletionError: any;
  isAdmin: boolean;
  isAddingSampleDatabase: boolean;
  addSampleDatabase: (query: any) => void;
  addSampleDatabaseError: boolean;
  children?: React.ReactNode;
}

export const DatabaseList = ({
  children,
  databases,
  addSampleDatabase,
  isAddingSampleDatabase,
  addSampleDatabaseError,
  engines,
  deletes,
  deletionError,
  isAdmin,
}: DatabaseListProps) => {
  const error = deletionError || addSampleDatabaseError;

  const hasSampleDatabase = useMemo(() => {
    return databases.some((db) => db.is_sample);
  }, [databases]);

  return (
    <>
      <div className={CS.wrapper} data-testid="database-list">
        <section className={cx(AdminS.PageHeader, CS.px2, CS.clearfix)}>
          <Flex justify="space-between" align="center">
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }} >
              <Box
                onClick={() => history.back()}
                style={{
                  backgroundColor: color("admin-navbar"),
                  borderRadius: "999px",
                  width: 34,
                  height: 34,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
>
                <Icon name="arrow_left" size={24} color={color("white")} />
              </Box>
              <h2 className={CS.m0} style={{fontSize: 18}}>{t`Databases`}</h2>
            </div>
            {isAdmin && (
              <Button
                variant="filled"
                component={Link}
                to="/admin/databases/create"
                leftSection={<Icon name="add_circle" size={24} />}
                styles={{
                  root: {
                    borderRadius: 999,
                    paddingInline: "1.5rem",
                    fontWeight: 400,
                    backgroundColor: "#03406C",
                    fontSize: "1.3rem",
                    height: "40px",
                  },
                }}
              >{t`Add Database`}</Button>
            )}
          </Flex>
        </section>
        {error && (
          <section>
            <FormMessage formError={error} />
          </section>
        )}
        <section style={{
          display: "flex",
          justifyContent: "center",
          backgroundColor: "var(--mb-color-bg-white)",
          borderRadius: "12px",
          boxShadow: "0 10px 22px rgba(0, 0, 0, 0.08)",
          width: "95vw",
          margin: "1rem",
          overflow: "hidden",

        }}>
          <Table
            columns={3}
            headers={[t`Name`, t`Engine`, t`Actions`]}
            rows={
              databases
                ? databases.map((database) => [
                    <Flex align="center" key={`name-${database.id}`}>
                      {!isSyncCompleted(database) && <Loader size="xs" mr="sm" />}
                      <Link
                        to={`/admin/databases/${database.id}`}
                        className={cx(CS.textBold, CS.link)}
                      >
                        {database.name}
                      </Link>
                    </Flex>,
                    engines?.[database.engine ?? ""]?.["driver-name"] ?? database.engine,
                    "actions"
                  ])
                : []
            }
            loadingState={
              databases ? null : (
                <>
                  <Loader size="1rem" />
                  <h3>{t`Loading ...`}</h3>
                </>
              )
            }
          />
          {!hasSampleDatabase && isAdmin ? (
            <div className={CS.pt4}>
              <span
                className={cx(CS.p2, CS.textItalic, {
                  [CS.borderTop]: databases && databases.length > 0,
                })}
              >
                {isAddingSampleDatabase ? (
                  <span className={cx(CS.textLight, CS.noDecoration)}>
                    {t`Restoring the sample database...`}
                  </span>
                ) : (
                  <UnstyledButton
                    c="brand"
                    onClick={() => addSampleDatabase(query)}
                  >
                    {t`Bring the sample database back`}
                  </UnstyledButton>
                )}
              </span>
            </div>
          ) : null}
        </section>
      </div>

      {children}
    </>
  );
};
